import * as React from 'react';
import { HomeTabCreateRequest } from '../../../../types/home/tab';
import Modal from '../../../../components/Modal';
import Form from '../../../../components/form/Form';
import { ValidationRule, WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import Panel from '../../../../components/Panel';
import Select from '../../../../components/input/Select';
import {
  HOME_TAB_LINK_TYPE_SELECT_LIST_V2,
  HOME_TAB_LINK_TYPE_V2,
  RECOMMENDATION_TAB_LINK_ID,
  RECOMMENDATION_TAB_LINK_URL,
  WEB_PAGE_TYPE_SELECT_LIST_V2,
  WEB_PAGE_TYPE_V2,
} from '../../../../constants/service/app/homeConstantsV2';
import { getWebPageTypeByUrl } from '../../../../lib/utils/homeTabUtils';
import Input from '../../../../components/input/Input';
import { InfiniteRangePicker } from '../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../types/common/date';
import TextArea from '../../../../components/input/TextArea/TextArea';
import LinkId from '../../../../components/input/LinkId';
import { eventReactTableColumns } from '../columns/subHomeColumns';
import { subHomeListFetcher } from '../fetchers/subHomeListFetcher';
import SearchListTrigger from '../../../../components/SearchListTrigger';
import { LinkItem } from '../../../../types/widget';
import { HomeItemV2 } from '../../../../types/home/list';
import { REQUIRED_MSG } from '../../../../constants/service/common/formRulesConstants';
import { validateStringLength } from '../../../../lib/utils/form/validators';
import { REX_NOT_TEXT } from '../../../../lib/rex';

export interface HomeTabListFormModalProps {
  form: WrappedFormUtils;
  initialValue?: Partial<HomeTabCreateRequest>;
  onSubmit: (tab: HomeTabCreateRequest) => void;
  onClose: () => void;
}

type HomeTabListFormRules = {
  title: { rules: ValidationRule[] };
  link: { rules: ValidationRule[] };
  linkType: { rules: ValidationRule[] };
  period: { rules: ValidationRule[] };
  memo: { rules: ValidationRule[] };
};

const homeTabListFormRules: HomeTabListFormRules = {
  title: {
    rules: [
      { required: true, type: 'string', message: REQUIRED_MSG },
      { validator: validateStringLength({ max: 8, message: '타이틀의 글자 수는 최대 8자입니다.' }) },
    ],
  },
  link: { rules: [{ required: true, type: 'object', message: REQUIRED_MSG }] },
  linkType: { rules: [{ required: true, type: 'string', message: REQUIRED_MSG }] },
  period: { rules: [{ required: true, type: 'object', message: REQUIRED_MSG }] },
  memo: { rules: [{ required: true, type: 'string', message: REQUIRED_MSG }] },
};

const HomeTabListFormModal = (props: HomeTabListFormModalProps): React.JSX.Element => {
  const { form, initialValue, onClose, onSubmit } = props;

  const link = form.getFieldValue('link') || initialValue?.link;
  const linkType = form.getFieldValue('linkType') || initialValue?.linkType;
  const webPageType = form.getFieldValue('webPageType') || getWebPageTypeByUrl(initialValue?.linkUrl);

  React.useEffect(() => {
    if (linkType === HOME_TAB_LINK_TYPE_V2.WEB_PAGE && webPageType === WEB_PAGE_TYPE_V2.RECOMMENDATION_TAB) {
      // 필드가 렌더링된 후에 값을 설정하기 위해 setTimeout 사용
      setTimeout(() => {
        form.setFieldsValue({ webPageType });
        form.setFieldsValue({ linkUrl: RECOMMENDATION_TAB_LINK_URL });
      }, 0);
    }
  }, [linkType, webPageType]);

  const handleSubmit = (): void => {
    form.validateFields((err, values) => {
      if (err) return;

      // 웹페이지 타입인 경우에는 linkId 연결이 없기 때문에 정해진 1 값을 추가해준다.
      if (values.linkType === HOME_TAB_LINK_TYPE_V2.WEB_PAGE) {
        values.link = { id: RECOMMENDATION_TAB_LINK_ID };
      }

      form.resetFields();
      onSubmit(values);
    });
  };

  const handleClose = (): void => {
    form.resetFields();
    onClose();
  };

  const defaultInitialValue = {
    linkType: HOME_TAB_LINK_TYPE_V2.SUB_PAGE,
    period: {
      startDate: null,
      endDate: null,
    },
  };

  const textNormalize = (text?: string): string | undefined => text?.replace(REX_NOT_TEXT, '').trimStart();

  return (
    <Modal
      title="홈 탭 등록"
      okText="저장"
      cancelText="취소"
      isOpen={true}
      width={700}
      height={600}
      isFooter={true}
      onOk={handleSubmit}
      onClose={handleClose}
    >
      <Form form={form} initialValue={initialValue || defaultInitialValue}>
        <Panel.Section>
          <Form.Item
            name="title"
            label="타이틀"
            maxCount={8}
            normalize={textNormalize}
            layoutType="vertical"
            rules={homeTabListFormRules.title.rules}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="linkType"
            label="링크 타입"
            layoutType="vertical"
            rules={homeTabListFormRules.linkType.rules}
          >
            <Select list={HOME_TAB_LINK_TYPE_SELECT_LIST_V2} />
          </Form.Item>
        </Panel.Section>
        {linkType !== HOME_TAB_LINK_TYPE_V2.WEB_PAGE && (
          <Panel.Section>
            <Form.Item name="link" label="링크 ID" layoutType="vertical" rules={homeTabListFormRules.link.rules}>
              <LinkId
                queryKey={['home', 'sub', 'list']}
                columns={eventReactTableColumns}
                fetcher={subHomeListFetcher}
                formatter={(home: HomeItemV2): LinkItem => ({ id: home.id, name: home.title })}
                trigger={(openModal: () => void): React.JSX.Element => (
                  <SearchListTrigger
                    value={link}
                    triggerText="ID 선택"
                    linkText={`${link?.name}(${link?.id})`}
                    onClick={openModal}
                  />
                )}
              />
            </Form.Item>
          </Panel.Section>
        )}
        {linkType === HOME_TAB_LINK_TYPE_V2.WEB_PAGE && (
          <Panel.Section>
            <Form.Item
              name="webPageType"
              label="웹 페이지 타입"
              layoutType="vertical"
              rules={[{ required: true, type: 'string', message: '웹 페이지 타입을 선택해주세요.' }]}
            >
              <Select list={WEB_PAGE_TYPE_SELECT_LIST_V2} />
            </Form.Item>
          </Panel.Section>
        )}
        {linkType === HOME_TAB_LINK_TYPE_V2.WEB_PAGE && webPageType === WEB_PAGE_TYPE_V2.RECOMMENDATION_TAB && (
          <Panel.Section>
            <Form.Item
              name="linkUrl"
              label="웹 URL(수정 불가)"
              layoutType="vertical"
              rules={[{ required: true, type: 'string', message: '웹 URL이 필요합니다.' }]}
            >
              <Input readOnly />
            </Form.Item>
          </Panel.Section>
        )}
        <Panel.Section>
          <Form.Item name="period" label="노출 기간" layoutType="vertical" rules={homeTabListFormRules.period.rules}>
            <InfiniteRangePicker format={DATE_FORMAT.FULL_DASH_FORMAT} showTime={true} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="memo"
            label="메모"
            normalize={textNormalize}
            layoutType="vertical"
            rules={homeTabListFormRules.memo.rules}
          >
            <TextArea />
          </Form.Item>
        </Panel.Section>
      </Form>
    </Modal>
  );
};

export default Form.create<HomeTabListFormModalProps>()(HomeTabListFormModal);
